import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { formatDate } from "../../../../utils/formatters";
import ContentSectionCard from "../../content/ContentSectionCard";
import { CONTENT_ACCENT, contentBodyCardsStackSx } from "../../content/contentEditorStyles";
import NewsletterHistoryHtmlPreview from "./NewsletterHistoryHtmlPreview";
import NewsletterHistoryRecipientsList from "./NewsletterHistoryRecipientsList";
import NewsletterHistoryErrorsTable from "./NewsletterHistoryErrorsTable";
import NewsletterHistoryStatusChip from "./NewsletterHistoryStatusChip";

const TAB_PREVIEW = 0;
const TAB_RECIPIENTS = 1;
const TAB_ERRORS = 2;

const detailTabsSx = {
  minHeight: 40,
  borderBottom: "1px solid #e2e8f0",
  bgcolor: "#f5f5f5",
  px: 1,
  "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 },
  "& .Mui-selected": { color: CONTENT_ACCENT },
  "& .MuiTabs-indicator": { backgroundColor: CONTENT_ACCENT },
};

const infoItemSx = {
  flex: "1 1 120px",
  minWidth: 0,
  textAlign: "center",
};

const InfoRow = ({ label, value, children }) => (
  <Box sx={infoItemSx}>
    <Typography
      variant="caption"
      color="text.secondary"
      display="block"
      sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}
    >
      {label}
    </Typography>
    {children || (
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
        {value || "—"}
      </Typography>
    )}
  </Box>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
};

const NewsletterHistoryDetailPanel = ({ detail }) => {
  const [tab, setTab] = useState(TAB_PREVIEW);

  if (!detail) {
    return null;
  }

  const errorCount = detail.recipients?.errors?.length || 0;
  const recipientCount = detail.recipients?.recipients?.length || 0;

  return (
    <Stack spacing={1} sx={{ ...contentBodyCardsStackSx, gap: 1 }}>
      <ContentSectionCard title="Informazioni">
        <Box sx={{ width: "100%", mb: 1.5, pb: 1.5, borderBottom: "1px solid #e2e8f0" }}>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}
          >
            Oggetto
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
            {detail.subject || "—"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-evenly",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 1.5,
            width: "100%",
          }}
        >
          <InfoRow label="Stato">
            <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
              <NewsletterHistoryStatusChip status={detail.status} />
            </Box>
          </InfoRow>
          <InfoRow label="Operatore" value={detail.operatorLabel || detail.loginId} />
          <InfoRow label="Creato il" value={formatDate(detail.createdAt)} />
          <InfoRow label="Inviato / programmato il" value={detail.sentAt ? formatDate(detail.sentAt) : "—"} />
          <InfoRow label="Destinatari" value={String(recipientCount)} />
          <InfoRow label="Errori" value={String(errorCount)} />
        </Box>
      </ContentSectionCard>

      <ContentSectionCard noPadding>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={detailTabsSx}>
          <Tab label="Anteprima HTML" />
          <Tab label={`Destinatari (${recipientCount})`} />
          <Tab label={`Errori (${errorCount})`} />
        </Tabs>
        <Box sx={{ p: 1.5 }}>
          {tab === TAB_PREVIEW ? (
            <NewsletterHistoryHtmlPreview
              subject={detail.subject}
              status={detail.status}
              htmlSnapshot={detail.htmlSnapshot}
              showHeader={false}
            />
          ) : null}
          {tab === TAB_RECIPIENTS ? (
            <NewsletterHistoryRecipientsList recipients={detail.recipients?.recipients} />
          ) : null}
          {tab === TAB_ERRORS ? <NewsletterHistoryErrorsTable errors={detail.recipients?.errors} /> : null}
        </Box>
      </ContentSectionCard>
    </Stack>
  );
};

NewsletterHistoryDetailPanel.propTypes = {
  detail: PropTypes.object,
};

export default NewsletterHistoryDetailPanel;
